import { useMutation, useQueryClient } from '@tanstack/react-query';
import { externalUsersApi } from '../../api/external-users.api';
import { notifications } from '../../utils/notifications';
import { NOTIFICATION_MESSAGES } from '../../constants/notification-messages';

// Re-export des hooks partagés (queries)
export { useExternalUsers, useExternalUser } from '@/features/external-users/hooks/useExternalUsers';

// Mutations spécifiques outil-pro (avec notifications)

export const useCreateExternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: externalUsersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      notifications.success(NOTIFICATION_MESSAGES.externalUser.createSuccess);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || NOTIFICATION_MESSAGES.error.generic;
      notifications.error(message);
    },
  });
};

export const useUpdateExternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      externalUsersApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['external-user', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      notifications.success(NOTIFICATION_MESSAGES.externalUser.updateSuccess);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || NOTIFICATION_MESSAGES.error.generic;
      notifications.error(message);
    },
  });
};

export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => externalUsersApi.toggleStatus(id),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      notifications.success(
        data.isActive ? 'Utilisateur activé avec succès' : 'Utilisateur désactivé avec succès',
      );
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || NOTIFICATION_MESSAGES.error.generic;
      notifications.error(message);
    },
  });
};

export const useDeleteExternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      externalUsersApi.delete(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      notifications.success(NOTIFICATION_MESSAGES.externalUser.deleteSuccess);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || NOTIFICATION_MESSAGES.error.generic;
      notifications.error(message);
    },
  });
};

export const useUpdateUserPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: string[] }) =>
      externalUsersApi.updatePermissions(id, permissions),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['external-user', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      notifications.success('Permissions mises à jour avec succès !');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || NOTIFICATION_MESSAGES.error.generic;
      notifications.error(message);
    },
  });
};

export const usePromoteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => externalUsersApi.promote(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['external-user', id] });
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      notifications.success('Utilisateur promu avec succès !');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || NOTIFICATION_MESSAGES.error.generic;
      notifications.error(message);
    },
  });
};

export const useSetPasswordForExternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      externalUsersApi.setPassword(id, password),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['external-user', id] });
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || NOTIFICATION_MESSAGES.error.generic;
      notifications.error(message);
    },
  });
};

export const useResendWelcomeEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => externalUsersApi.resendWelcomeEmail(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['external-user', id] });
      notifications.success('Email de bienvenue renvoyé avec succès');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || NOTIFICATION_MESSAGES.error.generic;
      notifications.error(message);
    },
  });
};
